import { DateTime } from 'luxon';
import moment from 'moment';
import { useCallback } from 'react';
import { CountryCode } from 'shared/constants/countries';
import { LOCALE } from 'shared/constants/localization/types';

import { getLocaleFromLocalStorage } from '@/services/localization';
import regionStore from '@/stores/userRegion';

export function useFormatDate() {
    const regionCode = regionStore.getRegion();

    const isUSRegion = regionCode === CountryCode.unitedStates;
    const locale = getLocaleFromLocalStorage() || LOCALE.en_AU;

    const formatDate = useCallback(
        (
            date: string,
            format:
                | 'date'
                | 'datetime'
                | 'time'
                | 'shortdatetime'
                | 'logdatetime',
        ) => {
            switch (format) {
                case 'date':
                    if (isUSRegion) {
                        return moment(date).format('MMMM Do YYYY');
                    } else {
                        return DateTime.fromISO(date)
                            .setLocale(locale)
                            .toLocaleString({
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            });
                    }

                case 'datetime':
                    if (isUSRegion) {
                        return moment(date).format('MMMM Do YYYY hh:mm a');
                    } else {
                        return DateTime.fromISO(date)
                            .setLocale(locale)
                            .toLocaleString({
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                            });
                    }
                case 'logdatetime':
                    if (!isUSRegion) {
                        return moment(date).format('MMM Do YYYY, hh:mm a');
                    } else {
                        return DateTime.fromISO(date)
                            .setLocale(locale)
                            .toLocaleString({
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                            });
                    }
                case 'time':
                    return DateTime.fromISO(date)
                        .setLocale(locale)
                        .toLocaleString({
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: locale !== LOCALE.nl_NL,
                        });
                case 'shortdatetime':
                    return DateTime.fromISO(date)
                        .setLocale(locale)
                        .toLocaleString({
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: locale !== LOCALE.nl_NL,
                        });
                default:
                    return DateTime.fromISO(date)
                        .setLocale(locale)
                        .toLocaleString();
            }
        },
        [locale, isUSRegion],
    );

    return formatDate;
}
